import { useState } from "react";
import axios from "axios";
import { Copy, Loader, Sparkles, Wand2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authed = () => {
  const t = localStorage.getItem("admin_session_token");
  return {
    withCredentials: true,
    headers: t ? { Authorization: `Bearer ${t}` } : {},
  };
};

const TASKS = [
  { key: "title", label: "SEO title", endpoint: "seo-title", defaultModel: "gpt" },
  { key: "meta", label: "Meta description", endpoint: "meta-description", defaultModel: "gpt" },
  { key: "faqs", label: "FAQ pairs", endpoint: "faqs", defaultModel: "claude" },
];

export default function AdminSeo() {
  const [brief, setBrief] = useState({
    topic: "Swedish massage in Bhubaneswar",
    primary_keyword: "swedish massage bhubaneswar",
    context:
      "Service page for Zorba International Spa (Chandrasekharpur, Bhubaneswar). " +
      "60 min ₹3,000 / 90 min ₹4,000 / 120 min ₹4,900. Light-to-medium pressure, " +
      "suited to first-time guests and anyone winding down after a long week.",
  });
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(null);
  const [modelPick, setModelPick] = useState({ title: "gpt", meta: "gpt", faqs: "claude" });

  const update = (k) => (e) => setBrief({ ...brief, [k]: e.target.value });

  async function run(task) {
    setLoading(task.key);
    try {
      const r = await axios.post(
        `${API}/admin/ai/${task.endpoint}`,
        { ...brief, model: modelPick[task.key] },
        authed(),
      );
      setResults((prev) => ({ ...prev, [task.key]: r.data }));
    } catch (err) {
      setResults((prev) => ({
        ...prev,
        [task.key]: { error: err?.response?.data?.detail || "AI call failed" },
      }));
    } finally {
      setLoading(null);
    }
  }

  const copy = (text) => navigator.clipboard?.writeText(text);

  return (
    <div data-testid="admin-seo-page">
      <p className="eyebrow"><Sparkles size={14} /> AI SEO Helper</p>
      <h1>Generate titles, descriptions &amp; FAQs.</h1>
      <p className="muted">
        Describe the page you're writing. The helper routes to Claude Sonnet or GPT depending on the task
        — pick a different model per task from the dropdown if you want to compare.
      </p>

      <div className="seo-form">
        <label>
          Page topic
          <input
            value={brief.topic}
            onChange={update("topic")}
            placeholder="e.g. Couples spa in Bhubaneswar"
            data-testid="seo-topic-input"
          />
        </label>
        <label>
          Primary keyword
          <input
            value={brief.primary_keyword}
            onChange={update("primary_keyword")}
            placeholder="e.g. couples spa bhubaneswar"
            data-testid="seo-keyword-input"
          />
        </label>
        <label>
          Context (prices, service details, anything verified)
          <textarea
            rows={4}
            value={brief.context}
            onChange={update("context")}
            placeholder="Paste the page's intro paragraph, verified prices, duration, nearby landmarks, etc."
            data-testid="seo-context-input"
          />
        </label>
      </div>

      <div className="seo-tasks">
        {TASKS.map((task) => {
          const r = results[task.key];
          const busy = loading === task.key;
          return (
            <section key={task.key} className="seo-task" data-testid={`seo-task-${task.key}`}>
              <header>
                <div>
                  <h3>{task.label}</h3>
                  <p className="muted-small">
                    {task.key === "title" && "Three options, each ≤60 characters."}
                    {task.key === "meta" && "Two options, 140–160 characters."}
                    {task.key === "faqs" && "Five Q&A pairs, grounded in the context you provided."}
                  </p>
                </div>
                <div className="seo-task-controls">
                  <label className="model-pick">
                    Model
                    <select
                      value={modelPick[task.key]}
                      onChange={(e) => setModelPick({ ...modelPick, [task.key]: e.target.value })}
                      data-testid={`seo-model-${task.key}`}
                    >
                      <option value="gpt">GPT-5</option>
                      <option value="claude">Claude Sonnet 4.5</option>
                    </select>
                  </label>
                  <button
                    className="btn btn-primary"
                    disabled={busy || !brief.topic}
                    onClick={() => run(task)}
                    data-testid={`seo-generate-${task.key}`}
                  >
                    {busy ? <Loader className="spin" size={15} /> : <Wand2 size={15} />}
                    {busy ? "Generating…" : "Generate"}
                  </button>
                </div>
              </header>

              {r?.error && <p className="error" data-testid={`seo-error-${task.key}`}>{r.error}</p>}

              {r && !r.error && task.key !== "faqs" && (
                <div className="seo-output">
                  <pre data-testid={`seo-result-${task.key}`}>{r.text}</pre>
                  <button className="text-link" onClick={() => copy(r.text)} data-testid={`seo-copy-${task.key}`}>
                    <Copy size={14} /> Copy all
                  </button>
                </div>
              )}

              {r && !r.error && task.key === "faqs" && (
                <div className="seo-output">
                  {r.faqs ? (
                    <ol className="seo-faq-list" data-testid={`seo-result-${task.key}`}>
                      {r.faqs.map((f, i) => (
                        <li key={i}>
                          <strong>{f.q}</strong>
                          <p>{f.a}</p>
                          <button
                            className="text-link"
                            onClick={() => copy(`Q: ${f.q}\nA: ${f.a}`)}
                            data-testid={`seo-copy-faq-${i}`}
                          >
                            <Copy size={13} /> Copy pair
                          </button>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <>
                      <p className="muted-small">Could not parse the AI response as JSON. Raw text:</p>
                      <pre>{r.raw}</pre>
                    </>
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
